import { useId, useState } from 'react';
import type { CurrencyCode } from '@ecobox/domain';
import { cx } from './cx';
import { minorToText, parseMoneyText } from './moneyInput';

export interface MoneyInputProps {
  /** Minor units (12550 for 125.50), or undefined when empty. */
  value: number | undefined;
  currency: CurrencyCode;
  /** Called with minor units, or undefined when the box is empty or not a valid amount. */
  onChange: (minor: number | undefined) => void;
  id?: string | undefined;
  'aria-label'?: string | undefined;
  'aria-describedby'?: string | undefined;
  invalid?: boolean | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  size?: 'md' | 'lg' | undefined;
  className?: string | undefined;
}

/** Type "125.50", get 12550 minor units. Shows the currency beside the box. */
export function MoneyInput({
  value,
  currency,
  onChange,
  id,
  invalid,
  disabled,
  required,
  size = 'md',
  className,
  ...aria
}: MoneyInputProps) {
  const [text, setText] = useState(() => (value === undefined ? '' : minorToText(value, currency)));
  const [problem, setProblem] = useState<string | null>(null);
  const problemId = useId();

  // Follow outside changes (a reset, another row) without fighting what the person is typing.
  const [seen, setSeen] = useState({ value, currency });
  if (seen.value !== value || seen.currency !== currency) {
    setSeen({ value, currency });
    const parsed = parseMoneyText(text, currency);
    if ((parsed.ok ? parsed.minor : undefined) !== value) {
      setText(value === undefined ? '' : minorToText(value, currency));
      setProblem(null);
    }
  }

  const change = (next: string): void => {
    setText(next);
    if (next.trim() === '') {
      setProblem(null);
      onChange(undefined);
      return;
    }
    const parsed = parseMoneyText(next, currency);
    if (parsed.ok) {
      setProblem(null);
      onChange(parsed.minor);
    } else {
      setProblem(parsed.error);
      onChange(undefined);
    }
  };

  const tidy = (): void => {
    const parsed = parseMoneyText(text, currency);
    if (parsed.ok) setText(minorToText(parsed.minor, currency));
  };

  return (
    <span className={cx('eb-money', className)}>
      <span className="eb-money__row">
        <input
          id={id}
          className="eb-input eb-money__input"
          data-size={size}
          inputMode="decimal"
          autoComplete="off"
          value={text}
          disabled={disabled}
          required={required}
          aria-invalid={invalid || problem ? true : undefined}
          aria-describedby={
            [problem ? problemId : undefined, aria['aria-describedby']].filter(Boolean).join(' ') ||
            undefined
          }
          aria-label={aria['aria-label']}
          onChange={(e) => change(e.target.value)}
          onBlur={tidy}
        />
        <span className="eb-money__cur" aria-hidden="true">
          {currency}
        </span>
      </span>
      {problem ? (
        <span id={problemId} className="eb-field__error" role="alert">
          {problem}
        </span>
      ) : null}
    </span>
  );
}
